import { useState, useEffect, useRef } from 'react';
import { Icon } from '../Icon.js';

interface ContactOption {
  id: number;
  name: string | null;
  email: string | null;
}

export interface ContactSelectorValue {
  id: number;
  name: string;
}

const LABEL_STYLE = {
  display: 'block' as const,
  marginBottom: '4px',
  fontSize: '11px',
  fontWeight: 500,
  color: 'var(--evo-fg-3)',
  fontFamily: 'var(--evo-font)',
} as const;

const HELPER_STYLE = {
  display: 'block' as const,
  marginTop: '4px',
  fontSize: '11px',
  fontFamily: 'var(--evo-font)',
} as const;

export interface ContactSelectorProps {
  value: ContactSelectorValue | null;
  onChange: (v: ContactSelectorValue | null) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: boolean | string;
  fullWidth?: boolean;
}

export function ContactSelector({
  value,
  onChange,
  disabled = false,
  placeholder = 'Buscar contacto…',
  label,
  helperText,
  error,
  fullWidth = false,
}: ContactSelectorProps) {
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [results, setResults] = useState<ContactOption[]>([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasError = !!error;
  const errorMsg = typeof error === 'string' ? error : undefined;

  useEffect(() => {
    if (disabled) return;
    const timer = setTimeout(() => setDebouncedQ(query), 250);
    return () => clearTimeout(timer);
  }, [query, disabled]);

  useEffect(() => {
    if (disabled) return;
    if (debouncedQ.length < 2) { setResults([]); setOpen(false); return; }
    fetch(`/api/contacts/search?q=${encodeURIComponent(debouncedQ)}&limit=8`)
      .then(r => r.json() as Promise<ContactOption[]>)
      .then(data => { setResults(data); setOpen(data.length > 0); setActiveIndex(-1); })
      .catch(() => { setResults([]); setOpen(false); });
  }, [debouncedQ, disabled]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function selectContact(c: ContactOption) {
    const displayName = c.name ?? c.email ?? String(c.id);
    onChange({ id: c.id, name: displayName });
    setQuery('');
    setOpen(false);
    setResults([]);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const r = results[activeIndex];
      if (r) selectContact(r);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  function handleClear(e: React.MouseEvent) {
    e.preventDefault();
    onChange(null);
    setQuery('');
    setResults([]);
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  const borderColor = hasError
    ? 'var(--evo-danger)'
    : focused || open ? 'var(--evo-cyan)'
    : 'var(--evo-stroke)';
  const shadow = focused || open
    ? `0 0 0 1px ${hasError ? 'var(--evo-danger)' : 'var(--evo-cyan)'}`
    : 'none';

  const containerStyle: React.CSSProperties = {
    display: fullWidth ? 'block' : 'inline-block',
    width: fullWidth ? '100%' : 'auto',
  };

  const boxStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    height: '32px',
    paddingLeft: '10px',
    paddingRight: '10px',
    background: 'var(--evo-bg-3)',
    border: `1px solid ${borderColor}`,
    borderRadius: 'var(--evo-r)',
    boxShadow: shadow,
    transition: 'border-color 120ms linear, box-shadow 120ms linear',
    boxSizing: 'border-box',
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled ? 0.7 : 1,
  };

  if (disabled && value) {
    return (
      <div style={containerStyle}>
        {label != null && <label style={LABEL_STYLE}>{label}</label>}
        <div style={{ ...boxStyle, cursor: 'not-allowed' }}>
          <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-3)', lineHeight: 0 }}>
            <Icon name="contacts" size={12} />
          </span>
          <span style={{
            fontSize: '13px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)',
            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {value.name}
          </span>
        </div>
        {(helperText != null || errorMsg != null) && (
          <span style={{ ...HELPER_STYLE, color: errorMsg != null ? 'var(--evo-danger)' : 'var(--evo-fg-3)' }}>
            {errorMsg ?? helperText}
          </span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ ...containerStyle, position: 'relative' }}>
      {label != null && <label style={LABEL_STYLE}>{label}</label>}
      <div style={boxStyle}>
        <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-3)', lineHeight: 0 }}>
          <Icon name="contacts" size={12} />
        </span>

        {value ? (
          <>
            <span style={{
              flex: 1, fontSize: '13px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {value.name}
            </span>
            <button
              type="button"
              onMouseDown={handleClear}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none', padding: '2px', cursor: 'pointer',
                color: 'var(--evo-fg-3)', flexShrink: 0, borderRadius: '3px',
              }}
            >
              <Icon name="close" size={10} />
            </button>
          </>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            autoComplete="off"
            style={{
              flex: 1, minWidth: 0, background: 'transparent', border: 'none',
              outline: 'none', color: 'var(--evo-fg-2)', fontSize: '13px',
              fontFamily: 'var(--evo-font)',
            }}
          />
        )}
      </div>

      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)',
          borderRadius: 'var(--evo-r)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          zIndex: 900, maxHeight: 180, overflowY: 'auto',
        }}>
          {results.map((r, i) => (
            <div
              key={r.id}
              onMouseDown={e => { e.preventDefault(); selectContact(r); }}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(-1)}
              style={{
                padding: '8px 10px', cursor: 'pointer',
                background: i === activeIndex ? 'var(--evo-bg-3)' : 'transparent',
                display: 'flex', alignItems: 'center', gap: '8px',
                borderBottom: i < results.length - 1 ? '1px solid var(--evo-stroke)' : 'none',
              }}
            >
              <span style={{
                fontSize: '13px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)',
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {r.name ?? r.email ?? '—'}
              </span>
              {r.name && r.email && (
                <span style={{ fontSize: '11px', color: 'var(--evo-fg-3)', flexShrink: 0 }}>
                  {r.email}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {(helperText != null || errorMsg != null) && (
        <span style={{ ...HELPER_STYLE, color: errorMsg != null ? 'var(--evo-danger)' : 'var(--evo-fg-3)' }}>
          {errorMsg ?? helperText}
        </span>
      )}
    </div>
  );
}
